import React, { useCallback, useState } from 'react';
import {
  bool, func, string, shape, arrayOf
} from 'prop-types';

import { noop } from '../../utils/promo-utils';
import { usePromoPresentation } from '../../contexts/PromoPresentationProvider';
import { usePromoModel } from '../../contexts/PromoModelProvider';
import { PromoSidecar } from '../promo-sidecar/PromoSidecar';
import { PromoQuickviewDetails } from './subcomponents/promo-quickview-details/PromoQuickviewDetails';
import { PromoQuickviewOverview } from './subcomponents/promo-quickview-overview/PromoQuickviewOverview';
import { PromoQuickviewSpecs } from './subcomponents/promo-quickview-specs/PromoQuickviewSpecs';
import { PromoQuickviewReviews } from './subcomponents/promo-quickview-reviews/PromoQuickviewReviews';
import './PromoQuickview.scss';

export const PromoQuickview = ({
  isQuickviewOpen,
  setIsQuickviewOpen,
  product,
  storeId,
  selected,
  onSelect,
  isInPromoDrawer,
  setQuantityWarningText,
  showQuantityWarningState
}) => {
  const { setIsDrawerOpen } = usePromoPresentation();
  const { isBogo } = usePromoModel();

  const closeDrawer = useCallback((event) => {
    if (event) event.preventDefault();
    setIsDrawerOpen(false);
    setIsQuickviewOpen(false);
  }, [setIsDrawerOpen, setIsQuickviewOpen]);

  const [sectionClicked, setSectionClicked] = useState({
    'product overview': false,
    specifications: false,
    reviews: false,
  });

  const itemId = product.itemId;
  const images = Array.isArray(product.media?.images) ? product.media.images : [];
  const brand = product.identifiers.brandName || '';
  const description = product.identifiers.productLabel || '';
  const overview = product.details?.description || '';
  const highlights = Array.isArray(product.details?.descriptiveAttributes)
    ? product.details.descriptiveAttributes : [];
  const averageRating = Number(product.reviews?.ratingsReviews?.averageRating) || 0;
  const totalRatings = product.reviews?.ratingsReviews?.totalReviews || '0';
  const onSectionClick = useCallback((section) => {
    if (!sectionClicked[section]) {
      setSectionClicked({ ...sectionClicked, [section]: true });
      window.LIFE_CYCLE_EVENT_BUS.trigger('promotion-products.quickview.section.click', {
        component: isBogo ? 'bogo' : 'multi-sku bulk',
        section: 'quick view',
        target: section,
      });
    }
  }, [isBogo, sectionClicked]);

  return (
    <PromoSidecar
      isSidecarOpen={isQuickviewOpen}
      setIsSidecarOpen={setIsQuickviewOpen}
      closeDrawer={closeDrawer}
      zIndex={1000}
      itemId={itemId}
      useSidecarNav
      useSidecarCta
      useSidecarMask={!isInPromoDrawer}
      selected={selected}
      onSelect={onSelect}
      setQuantityWarningText={setQuantityWarningText}
      showQuantityWarningState={showQuantityWarningState}
    >
      <article
        className="promo-quickview"
        data-testid={`promo-quickview${isQuickviewOpen ? '--open' : '--closed'}`}
      >
        {brand && description && (
          <>
            <PromoQuickviewDetails
              storeId={storeId}
              itemId={itemId}
              brand={brand}
              images={images}
              description={description}
            />
            <section className="promo-quickview-sections-container">
              <PromoQuickviewOverview
                itemId={itemId}
                overview={overview}
                highlights={highlights}
                closeDrawer={closeDrawer}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
              <PromoQuickviewSpecs
                itemId={itemId}
                storeId={storeId}
                closeDrawer={closeDrawer}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
              <PromoQuickviewReviews
                itemId={itemId}
                averageRating={averageRating}
                totalRatings={totalRatings}
                closeDrawer={closeDrawer}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
            </section>
          </>
        )}
      </article>
    </PromoSidecar>
  );
};

PromoQuickview.displayName = 'PromoQuickview';

PromoQuickview.defaultProps = {
  isInPromoDrawer: true,
  setQuantityWarningText: noop,
  showQuantityWarningState: false
};

PromoQuickview.propTypes = {
  selected: bool.isRequired,
  onSelect: func.isRequired,
  storeId: string.isRequired,
  isQuickviewOpen: bool.isRequired,
  setIsQuickviewOpen: func.isRequired,
  product: shape({
    itemId: string,
    media: shape({
      image: shape({
        url: string,
        type: string,
      }),
      images: arrayOf(shape({
        url: string,
        type: string,
      })),
    }),
    identifiers: shape({
      brand: string,
      description: string,
    }),
    details: shape({
      overview: string,
      highlights: arrayOf(string),
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string,
        totalReviews: string,
      }),
    }),
  }).isRequired,
  isInPromoDrawer: bool,
  setQuantityWarningText: func,
  showQuantityWarningState: bool
};
